/**
 * Auth store - Authentication state management.
 *
 * OAuth 2.0 with backend-generated JWT tokens.
 *
 * @module stores/auth
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import {
  configure,
  login as apiLogin,
  logout as apiLogout,
  parseCallbackTokens,
  clearUrlFragment,
  loadStoredAuth,
  storeAuth,
  clearStoredAuth,
  refreshAccessToken,
  getMe,
  isTokenExpired,
} from '@/api/auth'

// Refresh timer handle
let refreshTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Auth store.
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const expiresAt = ref<number | null>(null)
  const initialized = ref(false)
  const loading = ref(false)

  // Getters
  const isSignedIn = computed((): boolean => {
    if (!user.value || !accessToken.value || !expiresAt.value) {
      // Completed
      return false
    }
    // Completed
    return !isTokenExpired(expiresAt.value)
  })

  const hasPermission = computed(() => (permission: string): boolean => {
    if (!user.value?.permissions) {
      // Completed
      return false
    }
    // Completed
    return user.value.permissions.includes(permission) || user.value.permissions.includes('*')
  })

  // Actions

  /**
   * Schedule token refresh before expiration.
   *
   * @param expiresInSec - Seconds until expiration
   */
  function scheduleRefresh(expiresInSec: number): void {
    // Clear existing timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    // Refresh 60 seconds before expiration, minimum 10 seconds
    const refreshInSec = Math.max(10, expiresInSec - 60)
    console.info('[AuthStore] Scheduling token refresh in', refreshInSec, 'seconds')

    refreshTimer = setTimeout(async () => {
      await doRefresh()
    }, refreshInSec * 1000)
    // Completed
  }

  /**
   * Perform token refresh.
   */
  async function doRefresh(): Promise<boolean> {
    if (!refreshToken.value) {
      console.warn('[AuthStore] No refresh token available')
      // Completed
      return false
    }

    try {
      console.info('[AuthStore] Refreshing access token...')
      const tokens = await refreshAccessToken(refreshToken.value)

      // Update state
      accessToken.value = tokens.accessToken
      expiresAt.value = tokens.expiresAt

      // Update storage
      if (user.value) {
        storeAuth(tokens, user.value)
      }

      // Schedule next refresh
      scheduleRefresh(tokens.expiresIn)

      console.info('[AuthStore] Token refreshed successfully')
      // Completed
      return true
    } catch (error) {
      console.error('[AuthStore] Token refresh failed:', error)
      // Clear auth state on refresh failure
      await signOut()
      // Completed
      return false
    }
  }

  /**
   * Initialize auth from callback or storage.
   *
   * @param apiUrl - API base URL
   */
  async function init(apiUrl: string): Promise<void> {
    if (initialized.value) {
      // Completed
      return
    }

    loading.value = true
    console.info('[AuthStore] Initializing...')

    // Configure auth module
    configure({ apiUrl })

    try {
      // Check for OAuth callback tokens in URL fragment
      const callbackTokens = parseCallbackTokens()

      if (callbackTokens) {
        console.info('[AuthStore] Found callback tokens, fetching user info...')

        // Clear URL fragment
        clearUrlFragment()

        // Fetch user info
        const userInfo = await getMe(callbackTokens.accessToken)

        // Update state
        user.value = userInfo
        accessToken.value = callbackTokens.accessToken
        refreshToken.value = callbackTokens.refreshToken
        expiresAt.value = callbackTokens.expiresAt

        // Store auth
        storeAuth(callbackTokens, userInfo)

        // Schedule refresh
        scheduleRefresh(callbackTokens.expiresIn)

        console.info('[AuthStore] Initialized from OAuth callback')
      } else {
        // Try to restore from storage
        const stored = loadStoredAuth()

        if (stored) {
          user.value = stored.user
          refreshToken.value = stored.tokens.refreshToken

          // Check if access token is valid
          if (stored.tokens.accessToken && !isTokenExpired(stored.tokens.expiresAt)) {
            accessToken.value = stored.tokens.accessToken
            expiresAt.value = stored.tokens.expiresAt

            // Schedule refresh
            const remainingSec = stored.tokens.expiresAt - Math.floor(Date.now() / 1000)
            scheduleRefresh(remainingSec)

            console.info('[AuthStore] Restored from storage')
          } else {
            // Access token expired, try refresh
            console.info('[AuthStore] Access token expired, refreshing...')
            const refreshed = await doRefresh()

            if (!refreshed) {
              console.warn('[AuthStore] Could not refresh, clearing state')
              user.value = null
              refreshToken.value = null
            }
          }
        } else {
          console.info('[AuthStore] No stored auth found')
        }
      }
    } catch (error) {
      console.error('[AuthStore] Initialization error:', error)
      clearStoredAuth()
    }

    initialized.value = true
    loading.value = false
    console.info('[AuthStore] Initialization complete, signed in:', isSignedIn.value)
    // Completed
  }

  /**
   * Initiate OAuth login flow.
   *
   * @param redirectId - Redirect identifier (optional)
   */
  function login(redirectId?: string): void {
    console.info('[AuthStore] Initiating login...')
    apiLogin(redirectId)
    // Completed
  }

  /**
   * Sign out current user.
   */
  async function signOut(): Promise<void> {
    console.info('[AuthStore] Signing out...')

    // Clear refresh timer
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }

    // Call API logout
    if (accessToken.value) {
      await apiLogout(accessToken.value)
    } else {
      clearStoredAuth()
    }

    // Clear state
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    expiresAt.value = null

    console.info('[AuthStore] Signed out')
    // Completed
  }

  /**
   * Get valid access token, refreshing if needed.
   *
   * @returns Access token or null
   */
  async function getAccessToken(): Promise<string | null> {
    if (!accessToken.value || !expiresAt.value) {
      // Completed
      return null
    }

    // Refresh if expired or about to expire
    if (isTokenExpired(expiresAt.value)) {
      const refreshed = await doRefresh()
      if (!refreshed) {
        // Completed
        return null
      }
    }

    // Completed
    return accessToken.value
  }

  // Completed
  return {
    // State
    user,
    accessToken,
    refreshToken,
    expiresAt,
    initialized,
    loading,
    // Getters
    isSignedIn,
    hasPermission,
    // Actions
    init,
    login,
    signOut,
    getAccessToken,
  }
})

// Completed
